import { deleteMarkupBackward, insertNewlineContinueMarkupCommand } from "@codemirror/lang-markdown";
import { indentUnit, syntaxTree } from "@codemirror/language";
import { EditorSelection, countColumn, type EditorState, type Line, type StateCommand } from "@codemirror/state";
import type { SyntaxNode } from "@lezer/common";

const continueMarkup = insertNewlineContinueMarkupCommand({
  nonTightLists: false,
});

const LIST_ITEM_LINE = /^[\t ]*(?:[-+*](?:[\t ]+\[[ xX]\])?|\d+[.)])[\t ]+/;
const TASK_ITEM_PREFIX = /^[\t ]*[-+*][\t ]+\[[ xX]\](?:[\t ]+|$)/;
const MARKDOWN_TAB_SIZE = 4;

function orderedItem(state: EditorState, item: SyntaxNode) {
  const mark = item.getChild("ListMark");
  if (item.name !== "ListItem" || item.parent?.name !== "OrderedList" || !mark) return null;
  const line = state.doc.lineAt(mark.from);
  const marker = /^(\d+)([.)])$/.exec(state.sliceDoc(mark.from, mark.to));
  if (!marker) return null;
  const prefix = state.sliceDoc(line.from, mark.from);
  const column = countColumn(prefix, MARKDOWN_TAB_SIZE);
  const spacing = /^[\t ]*/.exec(state.sliceDoc(mark.to, line.to))![0];
  const markerEnd = countColumn(prefix + marker[0], MARKDOWN_TAB_SIZE);
  const padding = countColumn(prefix + marker[0] + spacing, MARKDOWN_TAB_SIZE) - markerEnd;
  return {
    item, mark, line, column, number: Number(marker[1]), delimiter: marker[2]!,
    contentColumn: markerEnd + (padding >= 1 && padding <= 4 ? padding : 1),
  };
}

function siblingItem(node: SyntaxNode | null, direction: "prevSibling" | "nextSibling") {
  while (node && node.name !== "ListItem") node = node[direction];
  return node;
}

function orderedItemOnLine(state: EditorState, line: Line) {
  const prefix = /^(?:[\t ]*>[\t ]?)*[\t ]*(?=\d{1,9}[.)](?:[\t ]|$))/.exec(line.text);
  if (!prefix) return null;
  let node: SyntaxNode | null = syntaxTree(state).resolveInner(line.from + prefix[0].length + 1, 1);
  while (node && node.name !== "ListItem") node = node.parent;
  const info = node && orderedItem(state, node);
  return info?.line.number === line.number ? info : null;
}

function listItemOnLine(state: EditorState, line: Line) {
  let item: SyntaxNode | null = syntaxTree(state).resolveInner(line.to, -1);
  while (item && item.name !== "ListItem") {
    if (item.name === "FencedCode" || item.name === "CodeBlock") return null;
    item = item.parent;
  }
  const mark = item?.getChild("ListMark");
  if (!item || !mark || state.doc.lineAt(mark.from).number !== line.number) return null;
  const after = state.sliceDoc(mark.to, line.to);
  // Keep task checkbox deletion on its existing path.
  if (/^[\t ]+\[[ xX]\]/.test(after)) return null;
  const spacing = /^[\t ]*/.exec(after)![0];
  const textFrom = mark.to + spacing.length;
  const markerColumn = countColumn(state.sliceDoc(line.from, mark.to), MARKDOWN_TAB_SIZE);
  const padding = countColumn(state.sliceDoc(line.from, textFrom), MARKDOWN_TAB_SIZE) - markerColumn;
  return { item, mark, line, textFrom, prefix: state.sliceDoc(line.from, mark.from),
    spacing: padding >= 1 && padding <= 4 ? spacing : " ",
    contentColumn: markerColumn + (padding >= 1 && padding <= 4 ? padding : 1),
    empty: textFrom === line.to && item.to <= line.to };
}

/** Join an empty list item with its following sibling, retaining only one marker. */
export const deleteMarkdownMarkupBackward: StateCommand = (target) => {
  const { state, dispatch } = target;
  if (state.readOnly) return false;
  const selection = state.selection;
  if (selection.ranges.length === 1 && selection.main.empty) {
    const cursor = selection.main.head;
    const line = state.doc.lineAt(cursor);
    const current = listItemOnLine(state, line);
    let empty = current?.empty && cursor >= current.mark.to ? current : null;
    let next = empty && line.number < state.doc.lines
      ? listItemOnLine(state, state.doc.line(line.number + 1)) : null;
    // The normal newline deletion would otherwise produce "- - item".
    if (!empty && current && (cursor === line.from || cursor === current.textFrom) && line.number > 1) {
      const previous = listItemOnLine(state, state.doc.line(line.number - 1));
      if (previous?.empty) { empty = previous; next = current; }
    }
    if (empty && next && empty.prefix === next.prefix
      && siblingItem(empty.item.nextSibling, "nextSibling")?.from === next.item.from) {
      const changes = [{ from: empty.mark.to, to: next.textFrom, insert: empty.spacing }];
      // Keep the moved item's continuation and children at the same depth when
      // the retained marker has a different width (for example, 9. and 10.).
      const shift = empty.contentColumn - next.contentColumn;
      if (shift) {
        for (let n = next.line.number + 1; n <= state.doc.lineAt(next.item.to).number; n++) {
          const childLine = state.doc.line(n);
          if (!childLine.text.trim()) continue;
          const quote = /^(?:[\t ]*>[\t ]?)*/.exec(childLine.text)![0];
          const indent = /^[\t ]*/.exec(childLine.text.slice(quote.length))![0];
          const column = countColumn(quote + indent, MARKDOWN_TAB_SIZE) + shift;
          changes.push({ from: childLine.from + quote.length,
            to: childLine.from + quote.length + indent.length,
            insert: " ".repeat(Math.max(0, column - countColumn(quote, MARKDOWN_TAB_SIZE))) });
        }
      }
      const lower = orderedItem(state, next.item);
      const upper = orderedItem(state, empty.item);
      if (lower && upper && lower.number === upper.number + 1) {
        let previous = lower.number;
        for (let node = siblingItem(next.item.nextSibling, "nextSibling"); node;
          node = siblingItem(node.nextSibling, "nextSibling")) {
          const sibling = orderedItem(state, node)!;
          if (sibling.number !== previous + 1) break;
          previous = sibling.number;
          changes.push({ from: sibling.mark.from, to: sibling.mark.to,
            insert: String(sibling.number - 1) + sibling.delimiter });
        }
      }
      dispatch(state.update({ changes,
        selection: EditorSelection.cursor(empty.mark.to + empty.spacing.length),
        scrollIntoView: true, userEvent: "delete.backward" }));
      return true;
    }
  }
  return deleteMarkupBackward(target);
};

/** Nest ordered items under their preceding sibling, with independent numbering. */
export const indentOrderedList: StateCommand = ({ state, dispatch }) => {
  if (state.readOnly) return false;
  const selectedLines = new Set<number>();
  for (const range of state.selection.ranges) {
    const last = state.doc.lineAt(range.to);
    const end = !range.empty && range.to === last.from ? last.number - 1 : last.number;
    for (let number = state.doc.lineAt(range.from).number; number <= end; number++) selectedLines.add(number);
  }
  const selected = new Map<number, NonNullable<ReturnType<typeof orderedItem>>>();
  for (const number of selectedLines) {
    const info = orderedItemOnLine(state, state.doc.line(number));
    if (info) selected.set(info.item.from, info);
  }
  const changes = new Map<number, { from: number; to: number; insert: string }>();
  const covered = new Set<number>();
  const moved = new Set<number>();
  const lists = new Map<number, SyntaxNode>();
  const destinations = new Map<number, { column: number; number: number }>();
  for (const info of selected.values()) {
    if (covered.has(info.line.number)) continue;
    let previous = siblingItem(info.item.prevSibling, "prevSibling");
    while (previous && selected.has(previous.from)) previous = siblingItem(previous.prevSibling, "prevSibling");
    const parent = previous && orderedItem(state, previous);
    if (!parent) continue;
    let destination = destinations.get(parent.item.from);
    if (!destination) {
      const children = parent.item.lastChild;
      const last = children?.name === "OrderedList" ? siblingItem(children.lastChild, "prevSibling") : null;
      const child = last && orderedItem(state, last);
      destination = child?.delimiter === info.delimiter
        ? { column: child.column, number: child.number + 1 }
        : { column: parent.contentColumn, number: 1 };
      destinations.set(parent.item.from, destination);
    }
    const number = String(destination.number++);
    const shift = destination.column - info.column;
    const childShift = shift + number.length - (info.mark.to - info.mark.from - 1);
    for (let lineNumber = info.line.number; lineNumber <= state.doc.lineAt(info.item.to).number; lineNumber++) {
      const line = state.doc.line(lineNumber);
      covered.add(lineNumber);
      const quote = /^(?:[\t ]*>[\t ]?)*/.exec(line.text)![0];
      const indent = /^[\t ]*/.exec(line.text.slice(quote.length))![0];
      const from = line.from + quote.length;
      if (lineNumber === info.line.number) {
        changes.set(lineNumber, { from, to: info.mark.to,
          insert: " ".repeat(Math.max(0, destination.column - countColumn(quote, MARKDOWN_TAB_SIZE))) + number + info.delimiter });
      } else if (line.text.trim()) {
        const column = countColumn(quote + indent, MARKDOWN_TAB_SIZE) + childShift;
        changes.set(lineNumber, { from, to: from + indent.length,
          insert: " ".repeat(Math.max(0, column - countColumn(quote, MARKDOWN_TAB_SIZE))) });
      }
    }
    moved.add(info.item.from);
    lists.set(info.item.parent!.from, info.item.parent!);
  }
  if (!moved.size) return false;
  // Keep an existing consecutive outer sequence consecutive after moving items out.
  for (const list of lists.values()) {
    let previous: number | null = null, removed = 0;
    for (let item = siblingItem(list.firstChild, "nextSibling"); item; item = siblingItem(item.nextSibling, "nextSibling")) {
      const info = orderedItem(state, item)!;
      if (previous !== null && info.number !== previous + 1) break;
      previous = info.number;
      if (moved.has(item.from)) removed++;
      else if (removed) changes.set(info.line.number, { from: info.mark.from, to: info.mark.to,
        insert: String(info.number - removed) + info.delimiter });
    }
  }
  for (const number of selectedLines) {
    if (covered.has(number)) continue;
    const line = state.doc.line(number);
    changes.set(number, { from: line.from, to: line.from, insert: state.facet(indentUnit) });
  }
  const changeSet = state.changes([...changes.values()].sort((a, b) => a.from - b.from));
  dispatch(state.update({ changes: changeSet, selection: state.selection.map(changeSet, 1),
    scrollIntoView: true, userEvent: "input.indent" }));
  return true;
};

/** Remove a full ordered-list nesting level rather than a fixed two spaces. */
export const outdentOrderedList: StateCommand = ({ state, dispatch }) => {
  if (state.readOnly || state.selection.ranges.length !== 1 || !state.selection.main.empty) return false;
  const line = state.doc.lineAt(state.selection.main.head);
  const info = orderedItemOnLine(state, line);
  const parentNode = info?.item.parent?.parent;
  const parent = parentNode && orderedItem(state, parentNode);
  if (!info || !parent || info.line.number !== line.number) return false;
  const number = String(parent.number + 1);
  const shift = parent.column - info.column;
  const childShift = shift + number.length - (info.mark.to - info.mark.from - 1);
  const changes: { from: number; to: number; insert: string }[] = [];
  for (let n = line.number; n <= state.doc.lineAt(info.item.to).number; n++) {
    const current = state.doc.line(n);
    const quote = /^(?:[\t ]*>[\t ]?)*/.exec(current.text)![0];
    const indent = /^[\t ]*/.exec(current.text.slice(quote.length))![0];
    const from = current.from + quote.length;
    const column = n === line.number ? parent.column : countColumn(quote + indent, MARKDOWN_TAB_SIZE) + childShift;
    if (current.text.trim()) changes.push({ from, to: n === line.number ? info.mark.to : from + indent.length,
      insert: " ".repeat(Math.max(0, column - countColumn(quote, MARKDOWN_TAB_SIZE)))
        + (n === line.number ? number + info.delimiter : "") });
  }
  let previous = parent.number;
  for (let node = siblingItem(parent.item.nextSibling, "nextSibling"); node; node = siblingItem(node.nextSibling, "nextSibling")) {
    const sibling = orderedItem(state, node)!;
    if (sibling.number !== previous + 1) break;
    previous = sibling.number;
    changes.push({ from: sibling.mark.from, to: sibling.mark.to, insert: String(sibling.number + 1) + sibling.delimiter });
  }
  const changeSet = state.changes(changes.sort((a, b) => a.from - b.from));
  dispatch(state.update({ changes: changeSet, selection: state.selection.map(changeSet, 1),
    scrollIntoView: true, userEvent: "delete.dedent" }));
  return true;
};

/** Move list items down at their start, and exit empty tasks in Live Preview. */
export const continueMarkdownMarkup = (target: Parameters<StateCommand>[0], livePreview = false): boolean => {
  const selection = target.state.selection;
  if (selection.ranges.length === 1 && selection.main.empty) {
    const cursor = selection.main.head;
    const line = target.state.doc.lineAt(cursor);
    const taskPrefix = livePreview ? TASK_ITEM_PREFIX.exec(line.text) : null;
    const emptyRenderedTask = taskPrefix !== null
      && line.text.slice(taskPrefix[0].length).trim() === "";
    if (emptyRenderedTask
      && cursor >= line.from + taskPrefix[0].indexOf("]") + 1) {
      target.dispatch(target.state.update({
        changes: { from: line.from, to: line.to },
        selection: EditorSelection.cursor(line.from),
        scrollIntoView: true,
      }));
      return true;
    }
    const atListStart = cursor === line.from && LIST_ITEM_LINE.test(line.text);
    const atRenderedTaskStart = taskPrefix !== null
      && !emptyRenderedTask
      && cursor <= line.from + taskPrefix[0].length;
    if (atListStart || atRenderedTaskStart) {
      target.dispatch(target.state.update({
        changes: { from: line.from, insert: "\n" },
        selection: EditorSelection.cursor(line.from + 1),
        scrollIntoView: true,
      }));
      return true;
    }
  }
  return continueMarkup(target);
};

type MarkdownListKind = "task" | "bullet" | "ordered";

interface ListPrefix {
  kind: MarkdownListKind;
  length: number;
}

function currentListPrefix(text: string): ListPrefix | null {
  if (/^[\t ]*[-+*][\t ]+\[[ xX]\][\t ]*$/.test(text)) {
    return { kind: "task", length: text.length };
  }
  if (/^[\t ]*[-+*][\t ]+$/.test(text)) {
    return { kind: "bullet", length: text.length };
  }
  if (/^[\t ]*\d+[.)][\t ]+$/.test(text)) {
    return { kind: "ordered", length: text.length };
  }
  return null;
}

function pastedListPrefix(text: string): ListPrefix | null {
  const task = /^[\t ]*(?:[-+*][\t ]+)?\[[ xX]\](?:[\t ]+|(?=\r?\n|$))/.exec(text);
  if (task) return { kind: "task", length: task[0].length };

  const bullet = /^[\t ]*[-+*](?:[\t ]+|(?=\r?\n|$))/.exec(text);
  if (bullet) return { kind: "bullet", length: bullet[0].length };

  const ordered = /^[\t ]*\d+[.)](?:[\t ]+|(?=\r?\n|$))/.exec(text);
  if (ordered) return { kind: "ordered", length: ordered[0].length };

  return null;
}

/** Avoid duplicating a list marker when pasting into an empty continued list item. */
export function normalizePastedListItem(lineBeforeCursor: string, pastedText: string): string {
  const current = currentListPrefix(lineBeforeCursor);
  const pasted = pastedListPrefix(pastedText);
  if (!current || !pasted || current.kind !== pasted.kind) return pastedText;
  return pastedText.slice(pasted.length);
}
